import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import "./index.scss";
import classNames from "../../../utils/classNames";

const GAP = 8; // viewport padding

const DropdownMenuFloat = ({ position, items = [], onClose }) => {
    const menuRef = useRef(null);
    const [coords, setCoords] = useState({ x: position?.x || 0, y: position?.y || 0 });

    useEffect(() => {
        let timers = [];
        // Defer binding so the opening click doesn't immediately close the menu
        const id = setTimeout(() => {
            const handleClickOutside = (event) => {
                const el = menuRef.current;
                if (el && el.contains(event.target)) return;
                onClose?.();
            };
            // Use capture to be robust across React synthetic propagation
            window.addEventListener("pointerdown", handleClickOutside, true);
            window.addEventListener("click", handleClickOutside, true);
            timers.push(() => window.removeEventListener("click", handleClickOutside, true));
            timers.push(() => window.removeEventListener("pointerdown", handleClickOutside, true));
        }, 0);
        return () => {
            clearTimeout(id);
            timers.forEach((fn) => fn());
        };
    }, [onClose]);

    // Close on any user-driven scroll/resize gesture
    useEffect(() => {
        const handleClose = () => onClose?.();
        window.addEventListener('scroll', handleClose, { passive: true });
        window.addEventListener('resize', handleClose);
        document.addEventListener('wheel', handleClose, { passive: true });
        document.addEventListener('touchmove', handleClose, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleClose);
            window.removeEventListener('resize', handleClose);
            document.removeEventListener('wheel', handleClose);
            document.removeEventListener('touchmove', handleClose);
        };
    }, [onClose]);

    // Clamp the menu within the viewport and avoid right/bottom overflow.
    useLayoutEffect(() => {
        const el = menuRef.current;
        const vw = typeof window !== 'undefined' ? window.innerWidth : 0;
        const vh = typeof window !== 'undefined' ? window.innerHeight : 0;
        if (!el || !vw || !vh) {
            setCoords({ x: position?.x || 0, y: position?.y || 0 });
            return;
        }
        const rect = el.getBoundingClientRect();
        let left = position?.x || 0;
        let top = position?.y || 0;
        // If overflowing right, shift left by menu width (anchored to the right of trigger)
        if (left + rect.width > vw - GAP) {
            left = Math.max(GAP, left - rect.width);
        }
        // If overflowing bottom, shift up by menu height
        if (top + rect.height > vh - GAP) {
            top = Math.max(GAP, top - rect.height);
        }
        // Ensure minimal padding from edges
        left = Math.min(Math.max(left, GAP), Math.max(vw - rect.width - GAP, GAP));
        top = Math.min(Math.max(top, GAP), Math.max(vh - rect.height - GAP, GAP));
        setCoords({ x: left, y: top });
    }, [position?.x, position?.y]);

    const handleItemClick = (event, item) => {
        // The menu is portaled, but React still bubbles its events through the
        // host component tree (e.g. the chat row that opened it) — keep menu
        // interactions from triggering host click handlers.
        event.stopPropagation();
        item.onClick?.();
        onClose?.();
    };

    const menu = (
        <div
            className="actions-dropdown-float"
            role="menu"
            style={{
                top: coords.y,
                left: coords.x,
            }}
            ref={menuRef}
            onClick={(event) => event.stopPropagation()}
        >
            {items.map((item) => (
                <button
                    key={item.label}
                    type="button"
                    className={classNames(
                        "actions-dropdown-float__item",
                        item.tone === "danger" && "actions-dropdown-float__item--danger"
                    )}
                    role="menuitem"
                    onClick={(event) => handleItemClick(event, item)}
                >
                    {item.icon && (
                        <span className="actions-dropdown-float__icon" aria-hidden="true">
                            {item.icon}
                        </span>
                    )}
                    <span className="actions-dropdown-float__label">{item.label}</span>
                </button>
            ))}
        </div>
    );

    return ReactDOM.createPortal(menu, document.getElementById("portal") || document.body);
};

export default DropdownMenuFloat;
